import { defineComponent as _defineComponent } from 'vue'
import { ref, computed } from 'vue'
import { useRoute, useRouter } from '@nuxtjs/composition-api'
import { useShowDebugging } from '~/functions/accounts'
import { renderMarkdown } from '~/functions/markdown'
import { unAssignedVentureIds } from '~/functions/sfdr'
import {
  type FundType,
  useAddFeedbackMutation,
  useGetAccountQuery,
  useGetFundIsInSyncQuery,
  useSyncVenturesWithSfdrFundSettingsMutation,
} from '~/types/types'
import { useNuxtApp } from '#app'

interface CompletionTableItems {
  section: string
  result: boolean
  items: {
    result: boolean
    why: string
  }
}


export default /*#__PURE__*/_defineComponent({
  __name: 'OutroStepper',
  props: {
    fund: null,
    mandatoryQuestionsAnswered: { type: Boolean },
    completionTableItems: null
  },
  emits: ["submit"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'submit'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  fund: FundType,
  mandatoryQuestionsAnswered: boolean,
  completionTableItems: CompletionTableItems[]
};





const route = useRoute()
const router = useRouter()
const { $i18n } = useNuxtApp()

const { mutate, onDone, onError, loading } = useSyncVenturesWithSfdrFundSettingsMutation()
const isSubmitting = ref(false)

onDone(() => {
  if (!isSubmitting.value) return
  // eslint-disable-next-line no-console
  console.log('onDone')
  isSubmitting.value = false
  step.value = 2
  emit('submit')
})

onError(() => {
  isSubmitting.value = false
})

const { result: isInSyncResult, loading: syncLoading, refetch } = useGetFundIsInSyncQuery(
  { id: props.fund?.id as any },
  { fetchPolicy: 'network-only' }
)

const syncState = computed(() => isInSyncResult.value?.getFund.syncState)

const isInSync = computed(
  () => isInSyncResult.value?.getFund?.isInSync || false,
)

const { result: accountResult } = useGetAccountQuery()

const unAssignedIds = computed(() => unAssignedVentureIds(props.fund))

const unAssignedVentures = computed(() =>
  accountResult.value?.getAccount?.ventureLinks
    ?.map((e) => e?.venture)
    ?.filter((e) => unAssignedIds.value.includes(e?.id)) || []
)

const feedback = ref({
  title: 'SFDR Setup',
  rating: 0,
  description: '',
  url: route.fullPath,
})

const showDebugging = useShowDebugging()
const showBeyondFundTable = ref(false)

const { mutate: addFeedback } = useAddFeedbackMutation()
const submitFeedback = async () => {
  await addFeedback({ payload: feedback.value }).then(() => {
    window.$nuxt.$emit('userMsg', {
      color: 'green',
      message: $i18n.t('feedback.thanks'),
    })
    feedback.value = {
      title: 'SFDR Setup',
      description: '',
      rating: 0,
      url: route.fullPath,
    }
    setTimeout(() => router.push(`/portfolio/metrics`), 2000)
  })
}

const changesIncludeHiddenKpi = computed(() => {
  return (
    syncState.value?.assignmentsToAdd?.some((assignment) =>
      props.fund?.portfolio?.hiddenKpis?.includes(assignment.metricId),
    ) || false
  )
})

const expanded = ref<string[]>([])
const hasFundVentureIds = (fund: any) =>
  fund && fund.ventureIds && fund.ventureIds.length > 0

const step = ref(1)
const ratingDescriptions = {
  0: 'No rating',
  1: 'Awful',
  2: 'Poor',
  3: 'Fair',
  4: 'Good',
  5: 'Excellent',
}

const explanationForDisabled = computed(() => {
  if (isInSync.value) {
    return 'No need to submit, all tasks have been assigned to your companies.'
  }
  return ''
})

const showNotificationDialog = ref(false)
const shouldPromptEsgNotification = computed(
  () => (syncState.value?.esgQuestionnairesToAdd?.length || 0) > 0,
)

function submitFund() {
  if (isSubmitting.value || loading.value) return
  if (shouldPromptEsgNotification.value) {
    showNotificationDialog.value = true
    return
  }
  confirmSubmitFund(false, '')
}

function confirmSubmitFund(sendEmails: boolean, note: string) {
  if (isSubmitting.value || loading.value) return
  if (props.fund?.id == null) {
    // eslint-disable-next-line no-console
    console.error('fund.id is missing')
    return
  }

  const variables: any = {
    fundId: props.fund.id,
    sendEsgNotifications: sendEmails,
  }
  if (sendEmails && note.trim().length > 0) {
    variables.esgNotificationNote = note.trim()
  }
  isSubmitting.value = true
  try {
    ;(mutate as any)(variables)
  } catch (_error) {
    isSubmitting.value = false
    throw _error
  }
}

function routeToResultView() {
  router.push(`/portfolio/funds/export-tabs/${props.fund?.id}`)
}

function routeHome() {
  router.push(`/portfolio/dashboard`)
}

// The unAssignedVentureIds helper is imported and can be used by template as needed

const beyondFundHeaders = computed(() => [
  { text: 'KPI', value: 'metric.name' },
  {
    text: $i18n.t('companyPerspective.kpis.indicatorSets') as string,
    value: 'metric.commaSeparatedSections',
  },
  { text: 'Company', value: 'assigneeId' },
  { text: 'Action', value: 'action' },
])


return { __sfc: true,props, emit, route, router, $i18n, mutate, onDone, onError, loading, isSubmitting, isInSyncResult, syncLoading, refetch, syncState, isInSync, accountResult, unAssignedIds, unAssignedVentures, feedback, showDebugging, showBeyondFundTable, addFeedback, submitFeedback, changesIncludeHiddenKpi, expanded, hasFundVentureIds, step, ratingDescriptions, explanationForDisabled, showNotificationDialog, shouldPromptEsgNotification, submitFund, confirmSubmitFund, routeToResultView, routeHome, beyondFundHeaders, renderMarkdown }
}

})